import { Injectable } from "@angular/core";
import { Status, translateCommonErrorStatus } from "@core/data";
import { ReportAssetOption, ReportDataContext, ReportImageOption } from "@core/data/types/advanced-report";
import { coerceMomentProperty } from "@core/utils";
import * as moment from "moment";
import { AdvancedReportCompilerService } from "@core/data/api/reports/advanced-report-compiler.service";
import { ShowErrorMessage } from "@app/auth";
import { Action, createSelector, Selector, State, StateContext } from "@ngxs/store";
import { forkJoin, Observable, of, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

// Discriminator for the picker's tabs. Epic 2 activates ORG/REPORT; only SAMPLE loads today.
export enum ImagePickerTab {
    SAMPLE = 'SAMPLE',
    ORG = 'ORG',
    REPORT = 'REPORT',
}

// each tab's options are stamped with the context they must persist as (AD-7).
// Record (not an index signature) so adding a tab without a context is a compile error, never a silent undefined.
export const CONTEXT_BY_TAB: Record<ImagePickerTab, ReportDataContext> = {
    [ImagePickerTab.SAMPLE]: ReportDataContext.SAMPLE_IMAGE,
    [ImagePickerTab.ORG]: ReportDataContext.ORG_ASSET,
    [ImagePickerTab.REPORT]: ReportDataContext.REPORT_ASSET,
};

export interface ImageTabSlice {
    options: ReportImageOption[];
    status: Status;
    // Pagination (Sample Photos tab only — ORG/REPORT always load in full, so these stay at their
    // defaults for those tabs). nextPage is the page to request next; loadingMore drives the infinite-scroll
    // spinner without touching `status`, so the grid already on screen never disappears behind a skeleton
    // while a further page is fetched. Optional: patchSlice()/EMPTY_SLICE always populate them at runtime —
    // optional only so hand-built test fixtures don't all need updating for a field they don't exercise.
    hasMore?: boolean;
    nextPage?: number;
    loadingMore?: boolean;
}

// Uniform shape loaderFor() resolves to regardless of tab, so the load/load-more actions never special-case one.
interface LoadedPage {
    options: ReportImageOption[];
    hasMore: boolean;
    nextPage: number;
}

export type BirthDateSort = 'asc' | 'desc';
export type SortAxis = 'birthDate' | 'label';

export interface ImageFilter {
    cultivarKeys: string[];
    siteKeys: string[];
    labelSearch: string;
    birthYear: number;
    // plain 'YYYY-MM-DD' days, not instants — the datepicker's moments are normalised on the way in
    evalFrom: string;
    evalTo: string;
}

// frozen: DEFAULTS hands this same object to every dialog instance
export const EMPTY_FILTER: ImageFilter = Object.freeze({
    cultivarKeys: Object.freeze([]) as string[],
    siteKeys: Object.freeze([]) as string[],
    labelSearch: null,
    birthYear: null,
    evalFrom: null,
    evalTo: null,
}) as ImageFilter;

export interface ImageTile {
    option: ReportImageOption;
    captureDate: string;
    isUploadDate: boolean;
}

export interface EvalGroup {
    evalKey: string;
    evalDate: string;
    hasDate: boolean;
    images: ImageTile[];
}

export interface SampleGroup {
    sampleKey: string;
    label: string;
    birthDate: string;
    evals: EvalGroup[];
}

export interface SiteGroup {
    siteKey: string;
    label: string;
    samples: SampleGroup[];
}

export interface CultivarGroup {
    cultivarKey: string;
    label: string;
    isUnknown: boolean;
    sites: SiteGroup[];
}

export interface ReportImageFormStateModel {
    activeTab: ImagePickerTab;
    imageKey: string;
    reportKey: string;
    ownerOrgKey: string;
    birthDateSort: BirthDateSort;
    sortAxis: SortAxis;
    filter: ImageFilter;
    slices: { [tab: string]: ImageTabSlice };
}

const EMPTY_SLICE: ImageTabSlice = {
    options: [],
    status: Status.UNINITIALIZED,
    hasMore: false,
    nextPage: 1,
    loadingMore: false,
};

const DEFAULTS: ReportImageFormStateModel = {
    activeTab: ImagePickerTab.SAMPLE,
    imageKey: null,
    reportKey: null,
    ownerOrgKey: null,
    birthDateSort: 'asc',
    sortAxis: 'birthDate',
    filter: EMPTY_FILTER,
    slices: {},
};

// moment, not Date: the API serializes PHP's DateTime::ISO8601 ('+0000', no colon), which Safari's Date parser rejects.
// Invalid/absent dates collapse to null so they land in the undated partition instead of poisoning the comparator with NaN.
function timeOf(value: string): number {
    let parsed = coerceMomentProperty(value);
    return parsed ? parsed.valueOf() : null;
}

/**
 * Dated items first (asc, or desc when reversed), undated always last; ties resolved deterministically.
 * Keys are parsed once up front — calling timeFn inside the comparator would reparse every date on every comparison.
 */
function orderByDate<T>(items: T[], timeFn: (item: T) => number, tieFn: (a: T, b: T) => number, desc = false): T[] {
    let keyed = items.map(item => ({ item, time: timeFn(item) }));
    let dated = keyed.filter(entry => entry.time !== null);
    let undated = keyed.filter(entry => entry.time === null);

    dated.sort((a, b) => {
        let diff = a.time - b.time;
        if (diff !== 0) return desc ? -diff : diff;
        return tieFn(a.item, b.item);
    });
    undated.sort((a, b) => tieFn(a.item, b.item));

    return [...dated, ...undated].map(entry => entry.item);
}

/** Alphabetical ordering with the same shape as orderByDate: direction flips the key, ties stay deterministic. */
function orderByText<T>(items: T[], textFn: (item: T) => string, tieFn: (a: T, b: T) => number, desc = false): T[] {
    return [...items].sort((a, b) => {
        let diff = compareText(textFn(a), textFn(b));
        if (diff !== 0) return desc ? -diff : diff;
        return tieFn(a, b);
    });
}

function compareText(a: string, b: string): number {
    return (a || '').localeCompare(b || '');
}

/**
 * Maps the filter form's raw value onto the state's filter contract: chips hold models (→ keys),
 * the datepickers hold Moments (→ plain 'YYYY-MM-DD' days). Exported so the mapping is directly testable.
 */
export function toFilterPatch(value: any): Partial<ImageFilter> {
    let day = (picked: any): string => picked ? moment(picked).format('YYYY-MM-DD') : null;

    return {
        cultivarKeys: (value.cultivars || []).map((cultivar: any) => cultivar.key),
        siteKeys: (value.sites || []).map((site: any) => site.key),
        labelSearch: value.labelSearch || null,
        birthYear: value.birthYear === undefined ? null : value.birthYear,
        evalFrom: day(value.evalFrom),
        evalTo: day(value.evalTo),
    };
}

/**
 * Narrows the loaded payload client-side — no request is ever issued for a filter change.
 * Reads structured fields only; a rendered label is never parsed to infer identity.
 * Exported for direct testing.
 */
export function filterOptions(options: ReportImageOption[], filter: ImageFilter): ReportImageOption[] {
    if (!options || !filter) return options || [];

    let search = (filter.labelSearch || '').trim().toLowerCase();
    // evalStartDate is a DATE column shipped as UTC midnight, and the filter bounds are plain days:
    // compare them as 'YYYY-MM-DD' strings so no browser timezone can shift the window by a day
    let from = filter.evalFrom || null;
    let to = filter.evalTo || null;

    return options.filter(option => {
        if (filter.cultivarKeys && filter.cultivarKeys.length && filter.cultivarKeys.indexOf(option.cultivarKey) === -1) return false;
        if (filter.siteKeys && filter.siteKeys.length && filter.siteKeys.indexOf(option.siteKey) === -1) return false;
        if (search && (option.sampleLabel || '').toLowerCase().indexOf(search) === -1) return false;

        if (filter.birthYear !== null && filter.birthYear !== undefined) {
            let year = yearOf(option.birthDate);
            if (year !== filter.birthYear) return false;
        }

        if (from !== null || to !== null) {
            let day = dayOf(option.evalStartDate);
            if (day === null) return false;
            if (from !== null && day < from) return false;
            if (to !== null && day > to) return false;
        }

        return true;
    });
}

// parseZone keeps the server's own offset: a DATE shipped as UTC midnight must never drift to the
// previous day just because the viewer sits west of Greenwich
function dayOf(value: string): string {
    if (!value) return null;
    let parsed = moment.parseZone(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

function yearOf(value: string): number {
    if (!value) return null;
    let parsed = moment.parseZone(value);
    return parsed.isValid() ? parsed.year() : null;
}

/** Distinct sample birth years present in the payload, newest first. */
export function birthYearsOf(options: ReportImageOption[]): number[] {
    let years = new Set<number>();
    (options || []).forEach(option => {
        let year = yearOf(option.birthDate);
        if (year !== null) years.add(year);
    });
    return Array.from(years).sort((a, b) => b - a);
}

/**
 * Builds the Cultivar → Sample → Evaluation tree from the flat payload.
 * Exported for direct testing; the selector is the only production caller.
 */
export function buildSampleTree(options: ReportImageOption[], sort: BirthDateSort, axis: SortAxis = 'birthDate'): CultivarGroup[] {
    let desc = sort === 'desc';
    let byCultivar = new Map<string, ReportImageOption[]>();

    (options || []).forEach(option => {
        let key = option.cultivarKey || '';
        if (!byCultivar.has(key)) byCultivar.set(key, []);
        byCultivar.get(key).push(option);
    });

    let groups: CultivarGroup[] = [];
    byCultivar.forEach((rows, cultivarKey) => {
        groups.push({
            cultivarKey: cultivarKey || null,
            label: cultivarKey ? (rows[0].cultivarLabel || 'Unknown') : 'Unknown cultivar',
            isUnknown: !cultivarKey,
            sites: buildSiteGroups(rows, desc, axis),
        });
    });

    let known = groups.filter(group => !group.isUnknown);
    let unknown = groups.filter(group => group.isUnknown);
    let tie = (a: CultivarGroup, b: CultivarGroup) => compareText(a.label, b.label) || compareText(a.cultivarKey, b.cultivarKey);

    // on the label axis a cultivar sorts by its own name; the Unknown bucket is still always last, both directions
    if (axis === 'label') {
        return [...orderByText(known, group => group.label, tie, desc), ...unknown];
    }

    // a cultivar sits on the birthDate axis via its earliest sample; the Unknown bucket is always last, both directions
    let ordered = orderByDate(known, group => earliestBirthDate(group), tie, desc);

    return [...ordered, ...unknown];
}

function earliestBirthDate(group: CultivarGroup): number {
    let earliest: number = null;
    group.sites.forEach(site => site.samples.forEach(sample => {
        let time = timeOf(sample.birthDate);
        if (time !== null && (earliest === null || time < earliest)) earliest = time;
    }));
    return earliest;
}

// Site is its own tier so the sample header can stay short — the block/name no longer has to ride along in every label
function buildSiteGroups(rows: ReportImageOption[], desc: boolean, axis: SortAxis): SiteGroup[] {
    let bySite = new Map<string, ReportImageOption[]>();
    rows.forEach(row => {
        let key = row.siteKey || '';
        if (!bySite.has(key)) bySite.set(key, []);
        bySite.get(key).push(row);
    });

    let sites: SiteGroup[] = [];
    bySite.forEach((siteRows, siteKey) => {
        sites.push({
            siteKey: siteKey || null,
            label: siteLabelOf(siteRows[0]),
            samples: buildSampleGroups(siteRows, desc, axis),
        });
    });

    // sites read alphabetically; the birthDate axis governs the cultivar and sample tiers only
    return sites.sort((a, b) => compareText(a.label, b.label) || compareText(a.siteKey, b.siteKey));
}

function siteLabelOf(row: ReportImageOption): string {
    if (!row.siteKey) return 'No site';
    // block first — it is the part an evaluator actually navigates by
    return [row.siteBlock, row.siteName].filter(part => !!part).join(' · ') || 'No site';
}

function buildSampleGroups(rows: ReportImageOption[], desc: boolean, axis: SortAxis): SampleGroup[] {
    let bySample = new Map<string, ReportImageOption[]>();
    rows.forEach(row => {
        let key = row.sampleKey || '';
        if (!bySample.has(key)) bySample.set(key, []);
        bySample.get(key).push(row);
    });

    let samples: SampleGroup[] = [];
    bySample.forEach((sampleRows, sampleKey) => {
        samples.push({
            sampleKey: sampleKey || null,
            // short label only: cultivar and site are their own headers now, so repeating them here is noise
            label: sampleRows[0].sampleShortLabel || sampleRows[0].sampleCode || 'Unnamed sample',
            birthDate: sampleRows[0].birthDate || null,
            evals: buildEvalGroups(sampleRows),
        });
    });

    let tie = (a: SampleGroup, b: SampleGroup) => compareText(a.label, b.label) || compareText(a.sampleKey, b.sampleKey);

    if (axis === 'label') {
        return orderByText(samples, sample => sample.label, tie, desc);
    }

    return orderByDate(samples, sample => timeOf(sample.birthDate), tie, desc);
}

// evaluations and images never reverse — the sort toggle moves the birthDate axis only (F-2.5, no cascade)
function buildEvalGroups(rows: ReportImageOption[]): EvalGroup[] {
    let byEval = new Map<string, ReportImageOption[]>();
    rows.forEach(row => {
        let key = row.evalKey || '';
        if (!byEval.has(key)) byEval.set(key, []);
        byEval.get(key).push(row);
    });

    let evals: EvalGroup[] = [];
    byEval.forEach((evalRows, evalKey) => {
        let images = buildTiles(evalRows);
        let startDate = evalRows[0].evalStartDate || null;
        evals.push({
            evalKey: evalKey || null,
            // an undated evaluation sorts by its earliest upload (the only date the payload carries for it);
            // hasDate keeps the chip honest so a borrowed date is never shown as the evaluation's own
            evalDate: startDate || earliestCreatedAt(evalRows),
            hasDate: !!startDate,
            images,
        });
    });

    return orderByDate(
        evals,
        group => timeOf(group.evalDate),
        (a, b) => compareText(a.evalKey, b.evalKey),
    );
}

function earliestCreatedAt(rows: ReportImageOption[]): string {
    let earliest: string = null;
    let earliestTime: number = null;
    rows.forEach(row => {
        let time = timeOf(row.createdAt);
        if (time !== null && (earliestTime === null || time < earliestTime)) {
            earliest = row.createdAt;
            earliestTime = time;
        }
    });
    return earliest;
}

function buildTiles(rows: ReportImageOption[]): ImageTile[] {
    let tiles: ImageTile[] = rows.map(option => ({
        option,
        captureDate: option.takenAt || option.createdAt || null,
        // with no date at all there is nothing to qualify — don't claim an upload date that isn't there
        isUploadDate: !option.takenAt && !!option.createdAt,
    }));

    return orderByDate(
        tiles,
        tile => timeOf(tile.captureDate),
        (a, b) => compareText(a.option.key, b.option.key),
    );
}

/** Maps a report_asset row onto the picker's shared option shape — no other consumer ever sees ReportAssetOption. */
export function assetToOption(asset: ReportAssetOption): ReportImageOption {
    return {
        key: asset.key,
        fileRef: asset.fileRef,
        name: asset.name,
    } as ReportImageOption;
}

export class InitReportImageForm {
    static readonly type = "[ReportImageForm] Init";
    constructor(public reportKey: string, public ownerOrgKey: string, public imageKey: string) {}
}

export class SetActiveTab {
    static readonly type = "[ReportImageForm] Set Active Tab";
    constructor(public tab: ImagePickerTab) {}
}

export class LoadImageFormOptions {
    static readonly type = "[ReportImageForm] Load";
    constructor(public tab: ImagePickerTab) {}
}

// Sample Photos tab only (server-paginated) — a no-op for ORG/REPORT, whose slices never set hasMore.
export class LoadMoreImageOptions {
    static readonly type = "[ReportImageForm] Load More";
    constructor(public tab: ImagePickerTab) {}
}

export class SetBirthDateSort {
    static readonly type = "[ReportImageForm] Set Birth Date Sort";
    constructor(public sort: BirthDateSort) {}
}

export class SetSortAxis {
    static readonly type = "[ReportImageForm] Set Sort Axis";
    constructor(public axis: SortAxis) {}
}

export class SetImageFilter {
    static readonly type = "[ReportImageForm] Set Filter";
    constructor(public filter: Partial<ImageFilter>) {}
}

export interface NamedUpload {
    name: string;
    file: File;
}

// Covers both ORG and REPORT tab assets — both upload immediately in a batch (multiple files per
// drop/browse), never staged/deferred. Keyed by ImagePickerTab, same pattern as RenameAsset.
export class UploadAssets {
    static readonly type = "[ReportImageForm] Upload Assets";
    constructor(public tab: ImagePickerTab, public files: NamedUpload[]) {}
}

// Covers both ORG and REPORT tab assets — the server's delete endpoint is identical for both scopes.
export class DeleteAsset {
    static readonly type = "[ReportImageForm] Delete Asset";
    constructor(public tab: ImagePickerTab, public key: string) {}
}

// Covers both ORG and REPORT tab assets — renaming is a first-class affordance on either tab's tiles now.
export class RenameAsset {
    static readonly type = "[ReportImageForm] Rename Asset";
    constructor(public tab: ImagePickerTab, public key: string, public name: string) {}
}

@State<ReportImageFormStateModel>({
    name: 'advanced_report_image_form',
    defaults: DEFAULTS,
})
@Injectable()
export class ReportImageFormState {

    @Selector()
    static activeTab(state: ReportImageFormStateModel): ImagePickerTab {
        return state.activeTab;
    }

    @Selector()
    static activeSlice(state: ReportImageFormStateModel): ImageTabSlice {
        return state.slices[state.activeTab] || EMPTY_SLICE;
    }

    @Selector()
    static birthDateSort(state: ReportImageFormStateModel): BirthDateSort {
        return state.birthDateSort;
    }

    @Selector()
    static sampleOptions(state: ReportImageFormStateModel): ReportImageOption[] {
        let slice = state.slices[ImagePickerTab.SAMPLE];
        return slice ? slice.options : EMPTY_SLICE.options;
    }

    @Selector()
    static sortAxis(state: ReportImageFormStateModel): SortAxis {
        return state.sortAxis;
    }

    @Selector()
    static filter(state: ReportImageFormStateModel): ImageFilter {
        return state.filter;
    }

    // birth-year choices come from the UNfiltered payload, so narrowing by year never empties its own dropdown
    static birthYearOptions = createSelector(
        [ReportImageFormState.sampleOptions],
        (options: ReportImageOption[]): number[] => birthYearsOf(options),
    );

    // grouping is sample-tab only; the asset tabs stay flat grids (Epic 2).
    // Composed over its real inputs so switching tabs (a new state object) doesn't rebuild the whole tree.
    // Filtering runs before grouping — an empty group can never be constructed, so no empty headers can render.
    static sampleTree = createSelector(
        [
            ReportImageFormState.sampleOptions,
            ReportImageFormState.filter,
            ReportImageFormState.birthDateSort,
            ReportImageFormState.sortAxis,
        ],
        (options: ReportImageOption[], filter: ImageFilter, sort: BirthDateSort, axis: SortAxis): CultivarGroup[] =>
            buildSampleTree(filterOptions(options, filter), sort, axis),
    );

    constructor(
        private _compilerService: AdvancedReportCompilerService,
    ) { }

    @Action(InitReportImageForm)
    initReportImageForm(ctx: StateContext<ReportImageFormStateModel>, action: InitReportImageForm) {
        ctx.setState({
            ...DEFAULTS,
            slices: {},
            imageKey: action.imageKey,
            reportKey: action.reportKey,
            ownerOrgKey: action.ownerOrgKey,
            activeTab: ImagePickerTab.SAMPLE,
        });

        ctx.dispatch(new LoadImageFormOptions(ImagePickerTab.SAMPLE));
    }

    @Action(SetBirthDateSort)
    setBirthDateSort(ctx: StateContext<ReportImageFormStateModel>, action: SetBirthDateSort) {
        ctx.patchState({ birthDateSort: action.sort });
    }

    @Action(SetSortAxis)
    setSortAxis(ctx: StateContext<ReportImageFormStateModel>, action: SetSortAxis) {
        ctx.patchState({ sortAxis: action.axis });
    }

    @Action(SetImageFilter)
    setImageFilter(ctx: StateContext<ReportImageFormStateModel>, action: SetImageFilter) {
        ctx.patchState({ filter: { ...ctx.getState().filter, ...action.filter } });
    }

    @Action(SetActiveTab)
    setActiveTab(ctx: StateContext<ReportImageFormStateModel>, action: SetActiveTab) {
        ctx.patchState({ activeTab: action.tab });

        let slice = ctx.getState().slices[action.tab];
        if (!slice || slice.status === Status.UNINITIALIZED) {
            ctx.dispatch(new LoadImageFormOptions(action.tab));
        }
    }

    @Action(LoadImageFormOptions)
    loadImageFormOptions(ctx: StateContext<ReportImageFormStateModel>, action: LoadImageFormOptions) {
        let loader = this.loaderFor(ctx.getState(), action.tab, 1);
        if (!loader) return;

        this.patchSlice(ctx, action.tab, { status: Status.LOADING });

        // catchError (not tap's error arm) so a failed load resolves the stream instead of surfacing to NGXS's global handler
        return loader.pipe(
            // stamp each option with its originating tab's context so the dialog's close contract never guesses (AD-7)
            map(result => ({
                ...result,
                options: result.options.map(option => ({ ...option, context: CONTEXT_BY_TAB[action.tab] })),
            })),
            tap(result => this.patchSlice(ctx, action.tab, {
                options: result.options,
                status: result.options.length ? Status.OK : Status.EMPTY,
                hasMore: result.hasMore,
                nextPage: result.nextPage,
            })),
            catchError(error => {
                this.patchSlice(ctx, action.tab, {
                    options: [],
                    status: translateCommonErrorStatus(error),
                    hasMore: false,
                    nextPage: 1,
                });
                return of(null);
            }),
        );
    }

    @Action(LoadMoreImageOptions)
    loadMoreImageOptions(ctx: StateContext<ReportImageFormStateModel>, action: LoadMoreImageOptions) {
        let state = ctx.getState();
        let slice = state.slices[action.tab] || EMPTY_SLICE;
        if (!slice.hasMore || slice.loadingMore) return;

        let loader = this.loaderFor(state, action.tab, slice.nextPage);
        if (!loader) return;

        this.patchSlice(ctx, action.tab, { loadingMore: true });

        return loader.pipe(
            map(result => ({
                ...result,
                options: result.options.map(option => ({ ...option, context: CONTEXT_BY_TAB[action.tab] })),
            })),
            tap(result => {
                let current = ctx.getState().slices[action.tab] || EMPTY_SLICE;
                this.patchSlice(ctx, action.tab, {
                    options: [...current.options, ...result.options],
                    hasMore: result.hasMore,
                    nextPage: result.nextPage,
                    loadingMore: false,
                });
            }),
            catchError(() => {
                // a failed "load more" leaves the already-loaded page intact — only the spinner clears,
                // and hasMore stays true so scrolling back down retries rather than silently giving up
                this.patchSlice(ctx, action.tab, { loadingMore: false });
                return of(null);
            }),
        );
    }

    // The seam that keeps slices additive: a new tab is a new case here + a new loader, nothing in the generic load action changes.
    // ORG/REPORT always resolve as a single complete "page" (hasMore false) — only the Sample Photos tab is server-paginated.
    private loaderFor(state: ReportImageFormStateModel, tab: ImagePickerTab, page: number): Observable<LoadedPage> {
        switch (tab) {
            case ImagePickerTab.SAMPLE:
                return this._compilerService.images(state.reportKey, state.ownerOrgKey, page).pipe(
                    map(result => ({
                        options: result.images || [],
                        hasMore: result.hasMore,
                        nextPage: result.page + 1,
                    })),
                );
            case ImagePickerTab.ORG:
                return this._compilerService.orgAssets(state.ownerOrgKey, page).pipe(
                    map(result => ({
                        options: (result.assets || []).map(assetToOption),
                        hasMore: result.hasMore,
                        nextPage: result.page + 1,
                    })),
                );
            case ImagePickerTab.REPORT:
                return this._compilerService.reportAssets(state.reportKey, page).pipe(
                    map(result => ({
                        options: (result.assets || []).map(assetToOption),
                        hasMore: result.hasMore,
                        nextPage: result.page + 1,
                    })),
                );
            default:
                return null;
        }
    }

    @Action(UploadAssets)
    uploadAssets(ctx: StateContext<ReportImageFormStateModel>, action: UploadAssets) {
        let state = ctx.getState();
        let uploadOne = (upload: NamedUpload) => action.tab === ImagePickerTab.REPORT
            ? this._compilerService.uploadReportAsset(state.reportKey, upload.name, upload.file)
            : this._compilerService.uploadOrgAsset(state.ownerOrgKey, upload.name, upload.file);

        // forkJoin, then a single patch for the whole batch — concurrent per-file taps would each read a
        // stale ctx.getState() and clobber one another's appended option (classic lost-update race).
        return forkJoin(action.files.map(uploadOne)).pipe(
            tap((assets: ReportAssetOption[]) => {
                let slice = ctx.getState().slices[action.tab] || EMPTY_SLICE;
                // stamped here, not just by loadImageFormOptions's generic pipe — these options never pass through
                // that pipe, so without this an uploaded-then-selected asset would carry no context at all (AD-7)
                let options = assets.map(asset => ({ ...assetToOption(asset), context: CONTEXT_BY_TAB[action.tab] }));
                this.patchSlice(ctx, action.tab, {
                    options: [...slice.options, ...options],
                    status: Status.OK,
                });
            }),
            catchError(error => {
                ctx.dispatch(new ShowErrorMessage("Couldn't upload the images. Please try again."));
                // rethrown (not swallowed to of(null)) so the dialog can tell success from failure and
                // only clear the staged files on success — the snackbar above still fires either way
                return throwError(error);
            }),
        );
    }

    @Action(RenameAsset)
    renameAsset(ctx: StateContext<ReportImageFormStateModel>, action: RenameAsset) {
        return this._compilerService.renameAsset(action.key, action.name).pipe(
            tap(asset => {
                let slice = ctx.getState().slices[action.tab] || EMPTY_SLICE;
                let options = slice.options.map(option => option.key === action.key ? { ...option, name: asset.name } : option);
                this.patchSlice(ctx, action.tab, { options });
            }),
            catchError(error => {
                ctx.dispatch(new ShowErrorMessage("Couldn't rename the image. Please try again."));
                return throwError(error);
            }),
        );
    }

    @Action(DeleteAsset)
    deleteAsset(ctx: StateContext<ReportImageFormStateModel>, action: DeleteAsset) {
        return this._compilerService.deleteAsset(action.key).pipe(
            tap(() => {
                let slice = ctx.getState().slices[action.tab] || EMPTY_SLICE;
                let options = slice.options.filter(option => option.key !== action.key);
                this.patchSlice(ctx, action.tab, {
                    options,
                    // an empty result must render the empty state, not a blank OK grid
                    status: options.length ? slice.status : Status.EMPTY,
                });
            }),
            catchError(error => {
                ctx.dispatch(new ShowErrorMessage("Couldn't delete the image. Please try again."));
                // rethrown so the dialog only clears a dangling selection after a confirmed successful delete
                return throwError(error);
            }),
        );
    }

    private patchSlice(ctx: StateContext<ReportImageFormStateModel>, tab: ImagePickerTab, patch: Partial<ImageTabSlice>) {
        let state = ctx.getState();
        // fresh base (not the EMPTY_SLICE singleton) so no slice ever aliases the shared default's array
        let base: ImageTabSlice = { options: [], status: Status.UNINITIALIZED, hasMore: false, nextPage: 1, loadingMore: false };
        ctx.patchState({
            slices: {
                ...state.slices,
                [tab]: { ...base, ...state.slices[tab], ...patch },
            },
        });
    }
}
